import type { LDProps } from 'launchdarkly-react-client-sdk';
import type { ReactElement } from 'react';
import type { HouseholdClientFragment } from 'src/components/advisor-dashboard/clients/fragments';
import type { FragmentType } from 'src/gql';
import type { NextPageWithLayout } from 'src/pages/_app';
import type { CategoryData } from 'src/types/category-data';
import type { TagData } from 'src/types/tag-data';
import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@savvy-kit/components/button';
import { Spinner } from '@savvy-kit/components/spinner';
import { TabsContent, Tabs as TabsPrimitive } from '@savvy-kit/components/tabs';
import PageContainer from '@savvy-kit/templates/page-container';
import PageTitle from '@savvy-kit/templates/page-title';
import { isNil } from 'es-toolkit';
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { SquareArrowOutUpRight } from 'lucide-react';
import { AccountsContent } from 'src/components/advisor-dashboard/accounts/accounts-content';
import ClientsListTable from 'src/components/advisor-dashboard/clients-list-table';
import { AdvisorDashboardAnnouncements } from 'src/components/advisor-dashboard/clients/advisor-dashboard-announcements';
import { ClientsTableCards } from 'src/components/advisor-dashboard/clients/clients-table-cards';
import {
  ClientsPaginationProvider,
  useClientsPagination,
} from 'src/components/advisor-dashboard/clients/pagination/clients-pagination-context';
import { useClientsUrlState } from 'src/components/advisor-dashboard/clients/utils/url-state-hooks';
import AdvisorDashTopNavLayout from 'src/components/advisor-dashboard/layout/advisor-dash-top-nav-layout';
import { TaxContent } from 'src/components/advisor-dashboard/taxes/tax-content';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';
import { graphql } from 'src/gql';
import { withAdvisorGuard } from 'src/hocs/with-advisor-guard';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useTrackPageView } from '@savvy/deprecated-shared/tracking/use-track-page-view';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';
import { assert } from '@savvy/utils/assert';

// Internal CSAs (ADMIN_CSA) and advisors are intentionally excluded.
const REVENUE_RESTRICTED_ROLES: readonly string[] = ['ASSISTANT', 'CSA'];

// Helper types for fragment-type casts
type HouseholdFragmentType = FragmentType<typeof HouseholdClientFragment>;

type ClientsTab = 'households' | 'accounts' | 'tax';

function isClientsTab(v: string): v is ClientsTab {
  return v === 'households' || v === 'accounts' || v === 'tax';
}

/* eslint-disable @typescript-eslint/consistent-type-assertions */
function extractPageInfo(data: unknown): {
  startCursor: string | null;
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
} | null {
  if (isNil(data)) return null;

  const pageInfoUnknown: unknown = data;
  const pageInfoRecord: Record<string, unknown> = pageInfoUnknown as Record<string, unknown>;
  const startCursor = pageInfoRecord['startCursor'];
  const endCursor = pageInfoRecord['endCursor'];
  const hasNextPage = pageInfoRecord['hasNextPage'];
  const hasPreviousPage = pageInfoRecord['hasPreviousPage'];

  return {
    startCursor: typeof startCursor === 'string' ? startCursor : null,
    endCursor: typeof endCursor === 'string' ? endCursor : null,
    hasNextPage: Boolean(hasNextPage),
    hasPreviousPage: Boolean(hasPreviousPage),
  };
}

function extractNodesFromEdges(edges: unknown): unknown[] {
  const edgesUnknown: unknown = edges;
  const edgesArray: unknown[] = !isNil(edgesUnknown) ? (edgesUnknown as unknown[]) : [];
  return edgesArray
    .map((edge: unknown) => {
      const edgeUnknown: unknown = edge;
      const edgeRecord: Record<string, unknown> = edgeUnknown as Record<string, unknown>;
      return edgeRecord['node'];
    })
    .filter((node: unknown) => !isNil(node));
}

function extractTotalCount(data: unknown): number | null {
  if (isNil(data)) return null;

  const dataRecord: Record<string, unknown> = data as Record<string, unknown>;
  const totalCount = dataRecord['totalCount'];

  return typeof totalCount === 'number' ? totalCount : null;
}

function castToHouseholdFragments(households: unknown[]): readonly HouseholdFragmentType[] {
  return households as unknown as readonly HouseholdFragmentType[];
}

// Helper to safely convert BigInt values from GraphQL to strings
function safeBigIntToString(value: unknown): string | null {
  if (isNil(value)) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
}
/* eslint-enable @typescript-eslint/consistent-type-assertions */

export const ClientsPageQuery = graphql(/* GraphQL */ `
  query ClientsPage(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $serviceCalendarV2: Boolean!
    $advisorIds: [ID!]
    $excludedAdvisorIds: [ID!]
    $query: String
    $reviewDue: Boolean
    $servicesDue: Boolean
    $sortBy: String
    $sortDirection: SortDirection
    $householdTags: [ID!]
    $excludedHouseholdTags: [ID!]
    $householdTagCategories: [ID!]
    $excludedHouseholdTagCategories: [ID!]
    $primaryAddressStates: [String!]
    $excludedPrimaryAddressStates: [String!]
    $lastContactDaysSince: [Int!]
    $excludedLastContactDaysSince: [Int!]
  ) {
    advisorDashboardClients(
      first: $first
      last: $last
      after: $after
      before: $before
      advisorIds: $advisorIds
      excludedAdvisorIds: $excludedAdvisorIds
      query: $query
      reviewDue: $reviewDue
      servicesDue: $servicesDue
      sortBy: $sortBy
      sortDirection: $sortDirection
      householdTags: $householdTags
      excludedHouseholdTags: $excludedHouseholdTags
      householdTagCategories: $householdTagCategories
      excludedHouseholdTagCategories: $excludedHouseholdTagCategories
      primaryAddressStates: $primaryAddressStates
      excludedPrimaryAddressStates: $excludedPrimaryAddressStates
      lastContactDaysSince: $lastContactDaysSince
      excludedLastContactDaysSince: $excludedLastContactDaysSince
    ) {
      totalCount
      totalSavvyAum
      totalAdvisorAnnualRevenue
      edges {
        node {
          ...HouseholdClient
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

export const ClientsAdvisorInfoQuery = graphql(/* GraphQL */ `
  query ClientsAdvisorInfo {
    currentUser {
      ...AdvisorDashboardAnnouncements
    }
    advisor {
      advisorGroups {
        id
        name
        representationType
        hideRevenueFromSupportRoles
        members {
          id
          user {
            id
            firstName
            lastName
            role
          }
        }
      }
      user {
        role
        firstName
        lastName
        id
      }
      viewAdvisorGroupClients
      organization {
        householdTags {
          id
          organizationHouseholdTag {
            id
            name
            isSmart
            organizationHouseholdCategory {
              id
              name
              hexColorCode
              emoji
            }
          }
        }
        organizationHouseholdCategories {
          id
          name
          hexColorCode
          emoji
          organizationHouseholdTags {
            id
            name
            isSmart
            householdCount
          }
        }
      }
    }
  }
`);

function HouseholdsContent() {
  const { user } = useAuth();
  assert(!isNil(user), 'User is not defined');

  const { currentAfter, currentBefore, currentFirst, currentLast, setCursors, setTotalItems, resetPagination } =
    useClientsPagination();
  const urlState = useClientsUrlState();
  const { flags } = useFlagsAndLoadingState();
  const { flagServiceCalendarV2 } = flags;

  const variables = useMemo(() => {
    const vars: Record<string, unknown> = {
      first: currentFirst,
      last: currentLast,
      after: currentAfter,
      before: currentBefore,
    };

    if (!isNil(urlState.advisor) && urlState.advisor.length > 0) {
      vars['advisorIds'] = urlState.advisor;
    }
    if (!isNil(urlState.advisorExcluded) && urlState.advisorExcluded.length > 0) {
      vars['excludedAdvisorIds'] = urlState.advisorExcluded;
    }

    if (!isNil(urlState.query) && urlState.query !== '') {
      vars['query'] = urlState.query;
    }
    if (!isNil(urlState.reviewDue)) {
      vars['reviewDue'] = urlState.reviewDue;
    }
    if (!isNil(urlState.servicesDue)) {
      vars['servicesDue'] = urlState.servicesDue;
    }
    if (!isNil(urlState.sortBy) && urlState.sortBy !== '') {
      vars['sortBy'] = urlState.sortBy;
    }
    if (!isNil(urlState.sortByOrder)) {
      vars['sortDirection'] = urlState.sortByOrder;
    }
    if (!isNil(urlState.householdTags) && urlState.householdTags.length > 0) {
      vars['householdTags'] = urlState.householdTags;
    }
    if (!isNil(urlState.householdTagsExcluded) && urlState.householdTagsExcluded.length > 0) {
      vars['excludedHouseholdTags'] = urlState.householdTagsExcluded;
    }
    if (!isNil(urlState.householdTagCategories) && urlState.householdTagCategories.length > 0) {
      vars['householdTagCategories'] = urlState.householdTagCategories;
    }
    if (!isNil(urlState.householdTagCategoriesExcluded) && urlState.householdTagCategoriesExcluded.length > 0) {
      vars['excludedHouseholdTagCategories'] = urlState.householdTagCategoriesExcluded;
    }
    if (urlState.primaryAddressStates && urlState.primaryAddressStates.length > 0) {
      vars['primaryAddressStates'] = urlState.primaryAddressStates;
    }
    if (urlState.primaryAddressStatesExcluded && urlState.primaryAddressStatesExcluded.length > 0) {
      vars['excludedPrimaryAddressStates'] = urlState.primaryAddressStatesExcluded;
    }
    if (urlState.lastContactDaysSince && urlState.lastContactDaysSince.length > 0) {
      vars['lastContactDaysSince'] = urlState.lastContactDaysSince
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => !Number.isNaN(value));
    }
    if (urlState.lastContactDaysSinceExcluded && urlState.lastContactDaysSinceExcluded.length > 0) {
      vars['excludedLastContactDaysSince'] = urlState.lastContactDaysSinceExcluded
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => !Number.isNaN(value));
    }

    return { ...vars, serviceCalendarV2: Boolean(flagServiceCalendarV2) };
  }, [
    currentFirst,
    currentLast,
    currentAfter,
    currentBefore,
    flagServiceCalendarV2,
    urlState.advisor,
    urlState.query,
    urlState.reviewDue,
    urlState.servicesDue,
    urlState.sortBy,
    urlState.sortByOrder,
    urlState.householdTags,
    urlState.householdTagsExcluded,
    urlState.advisorExcluded,
    urlState.householdTagCategories,
    urlState.householdTagCategoriesExcluded,
    urlState.primaryAddressStates,
    urlState.primaryAddressStatesExcluded,
    urlState.lastContactDaysSince,
    urlState.lastContactDaysSinceExcluded,
  ]);

  const { data, loading, error } = useQueryWithErrorLogging(ClientsPageQuery, {
    variables,
  });

  const {
    data: advisorInfoData,
    loading: advisorInfoLoading,
    error: advisorInfoError,
  } = useQueryWithErrorLogging(ClientsAdvisorInfoQuery);

  useEffect(() => {
    const pageInfo = data?.advisorDashboardClients.pageInfo;
    const extracted = extractPageInfo(pageInfo);
    if (!isNil(extracted)) {
      setCursors(extracted);
    }

    const clientsData = data?.advisorDashboardClients;
    if (!isNil(clientsData)) {
      const totalCount = extractTotalCount(clientsData);
      if (!isNil(totalCount)) {
        setTotalItems(totalCount);
      } else {
        setTotalItems(1000);
      }
    }
  }, [data, setCursors, setTotalItems]);

  const prevFiltersRef = useRef({
    query: urlState.query,
    reviewDue: urlState.reviewDue,
    servicesDue: urlState.servicesDue,
    advisor: urlState.advisor,
    advisorExcluded: urlState.advisorExcluded,
    householdTags: urlState.householdTags,
    householdTagsExcluded: urlState.householdTagsExcluded,
    householdTagCategories: urlState.householdTagCategories,
    householdTagCategoriesExcluded: urlState.householdTagCategoriesExcluded,
    primaryAddressStates: urlState.primaryAddressStates,
    primaryAddressStatesExcluded: urlState.primaryAddressStatesExcluded,
    lastContactDaysSince: urlState.lastContactDaysSince,
    lastContactDaysSinceExcluded: urlState.lastContactDaysSinceExcluded,
    sortBy: urlState.sortBy,
    sortByOrder: urlState.sortByOrder,
  });

  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.query !== urlState.query ||
      prevFiltersRef.current.reviewDue !== urlState.reviewDue ||
      prevFiltersRef.current.servicesDue !== urlState.servicesDue ||
      prevFiltersRef.current.sortBy !== urlState.sortBy ||
      prevFiltersRef.current.sortByOrder !== urlState.sortByOrder ||
      JSON.stringify(prevFiltersRef.current.advisor) !== JSON.stringify(urlState.advisor) ||
      JSON.stringify(prevFiltersRef.current.advisorExcluded) !== JSON.stringify(urlState.advisorExcluded) ||
      JSON.stringify(prevFiltersRef.current.householdTags) !== JSON.stringify(urlState.householdTags) ||
      JSON.stringify(prevFiltersRef.current.householdTagsExcluded) !== JSON.stringify(urlState.householdTagsExcluded) ||
      JSON.stringify(prevFiltersRef.current.householdTagCategories) !==
        JSON.stringify(urlState.householdTagCategories) ||
      JSON.stringify(prevFiltersRef.current.householdTagCategoriesExcluded) !==
        JSON.stringify(urlState.householdTagCategoriesExcluded) ||
      JSON.stringify(prevFiltersRef.current.primaryAddressStates) !== JSON.stringify(urlState.primaryAddressStates) ||
      JSON.stringify(prevFiltersRef.current.primaryAddressStatesExcluded) !==
        JSON.stringify(urlState.primaryAddressStatesExcluded) ||
      JSON.stringify(prevFiltersRef.current.lastContactDaysSince) !== JSON.stringify(urlState.lastContactDaysSince) ||
      JSON.stringify(prevFiltersRef.current.lastContactDaysSinceExcluded) !==
        JSON.stringify(urlState.lastContactDaysSinceExcluded);

    if (filtersChanged) {
      prevFiltersRef.current = {
        query: urlState.query,
        reviewDue: urlState.reviewDue,
        servicesDue: urlState.servicesDue,
        advisor: urlState.advisor,
        advisorExcluded: urlState.advisorExcluded,
        householdTags: urlState.householdTags,
        householdTagsExcluded: urlState.householdTagsExcluded,
        householdTagCategories: urlState.householdTagCategories,
        householdTagCategoriesExcluded: urlState.householdTagCategoriesExcluded,
        primaryAddressStates: urlState.primaryAddressStates,
        primaryAddressStatesExcluded: urlState.primaryAddressStatesExcluded,
        lastContactDaysSince: urlState.lastContactDaysSince,
        lastContactDaysSinceExcluded: urlState.lastContactDaysSinceExcluded,
        sortBy: urlState.sortBy,
        sortByOrder: urlState.sortByOrder,
      };
      resetPagination();
    }
  }, [
    urlState.query,
    urlState.reviewDue,
    urlState.servicesDue,
    urlState.advisor,
    urlState.advisorExcluded,
    urlState.householdTags,
    urlState.householdTagsExcluded,
    urlState.householdTagCategories,
    urlState.householdTagCategoriesExcluded,
    urlState.primaryAddressStates,
    urlState.primaryAddressStatesExcluded,
    urlState.lastContactDaysSince,
    urlState.lastContactDaysSinceExcluded,
    urlState.sortBy,
    urlState.sortByOrder,
    resetPagination,
  ]);

  const advisorInfo = advisorInfoData?.advisor ?? null;

  const tagsData = useMemo((): readonly TagData[] => {
    const organization = advisorInfo?.organization;
    if (!organization?.householdTags) {
      return [];
    }

    const tagMap = new Map<string, TagData>();

    organization.householdTags.forEach((householdTag) => {
      const orgTag = householdTag.organizationHouseholdTag;
      if (orgTag.id) {
        if (!tagMap.has(orgTag.id)) {
          const category = orgTag.organizationHouseholdCategory;
          const hexColorCode = typeof category.hexColorCode === 'string' ? category.hexColorCode : null;
          const emoji = typeof category.emoji === 'string' ? category.emoji : null;
          const categoryId = category.id;
          tagMap.set(orgTag.id, {
            id: orgTag.id,
            name: orgTag.name,
            hexColorCode,
            emoji,
            categoryId,
            isSmart: orgTag.isSmart,
          });
        }
      }
    });

    return Array.from(tagMap.values());
  }, [advisorInfo]);

  const categoriesData = useMemo((): readonly CategoryData[] => {
    const organization = advisorInfo?.organization;
    if (!organization?.organizationHouseholdCategories) {
      return [];
    }

    return organization.organizationHouseholdCategories.map((category) => {
      const hexColorCode = typeof category.hexColorCode === 'string' ? category.hexColorCode : null;
      const emoji = typeof category.emoji === 'string' ? category.emoji : null;

      return {
        id: category.id,
        name: category.name,
        hexColorCode,
        emoji,
        organizationHouseholdTags: (category.organizationHouseholdTags ?? []).map((tag) => ({
          id: tag.id,
          householdCount: typeof tag.householdCount === 'number' ? tag.householdCount : 0,
        })),
      };
    });
  }, [advisorInfo]);

  // Full org tag catalog (with names), used by the bulk Add Tags picker.
  const catalogTagsData = useMemo((): readonly TagData[] => {
    const categories = advisorInfo?.organization?.organizationHouseholdCategories ?? [];
    return categories.flatMap((category) => {
      const hexColorCode = typeof category.hexColorCode === 'string' ? category.hexColorCode : null;
      const emoji = typeof category.emoji === 'string' ? category.emoji : null;
      return (category.organizationHouseholdTags ?? []).map((tag) => ({
        id: tag.id,
        name: tag.name,
        hexColorCode,
        emoji,
        categoryId: category.id,
        isSmart: tag.isSmart,
      }));
    });
  }, [advisorInfo]);

  const edges = data?.advisorDashboardClients.edges;
  const householdsRaw = extractNodesFromEdges(edges);

  const totalSavvyAum = data?.advisorDashboardClients.totalSavvyAum;
  const totalAdvisorAnnualRevenue = data?.advisorDashboardClients.totalAdvisorAnnualRevenue;
  const totalClientsCount = data?.advisorDashboardClients.totalCount;

  if ((loading || advisorInfoLoading) && isNil(data) && isNil(advisorInfoData)) {
    return (
      <div className="my-4 flex flex-grow justify-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  if (error || advisorInfoError) {
    console.error('Error loading clients:', error || advisorInfoError);
    return <div>Error loading client list</div>;
  }

  const advisorGroups = advisorInfo?.advisorGroups ?? null;
  const advisorGroupsArray = advisorGroups ?? [];

  const revenueSettingGroup = advisorGroupsArray.find((group) => group.representationType === 'group') ?? null;
  const hideArr =
    (revenueSettingGroup?.hideRevenueFromSupportRoles ?? false) && REVENUE_RESTRICTED_ROLES.includes(user.role ?? '');

  const allHouseholds = castToHouseholdFragments(householdsRaw);

  const aggregateStats = {
    totalSavvyAum: safeBigIntToString(totalSavvyAum),
    totalAdvisorAnnualRevenue: safeBigIntToString(totalAdvisorAnnualRevenue),
    clientsCount: totalClientsCount ?? 0,
  };

  return (
    <div className="flex flex-col gap-6">
      <AdvisorDashboardAnnouncements currentUserFrag={advisorInfoData?.currentUser} />
      <ClientsTableCards aggregateStats={aggregateStats} loading={loading} hideArr={hideArr} />
      <ClientsListTable
        households={allHouseholds}
        advisorGroups={advisorGroupsArray}
        advisorInfo={advisorInfo}
        loading={loading}
        totalCount={totalClientsCount ?? 0}
        tags={tagsData}
        tagCatalog={catalogTagsData}
        categories={categoriesData}
        hideArr={hideArr}
      />
    </div>
  );
}

function ClientsPageTabs() {
  const router = useRouter();
  const { aprilTax, taxV3 } = useFlags();

  const queryTab = typeof router.query['tab'] === 'string' ? router.query['tab'] : 'households';
  const activeTab: ClientsTab = isClientsTab(queryTab) ? queryTab : 'households';

  const setTab = (tab: string) => {
    if (isClientsTab(tab)) {
      void router.push({ pathname: router.pathname, query: { ...router.query, tab } }, undefined, { shallow: true });
    }
  };

  const pageTitles: Record<ClientsTab, string> = {
    households: 'Households',
    accounts: 'Accounts',
    tax: 'Tax',
  };

  const actionButtons =
    activeTab === 'tax' && taxV3 === true ? (
      <Button
        size="sm"
        variant="secondary"
        icon={<SquareArrowOutUpRight />}
        onClick={() => {
          window.open('https://www.getapril.com/', '_blank', 'noopener,noreferrer');
        }}
      >
        Launch Tax Filing
      </Button>
    ) : null;

  const tabs = [
    { name: 'Households', value: 'households' },
    { name: 'Accounts', value: 'accounts' },
    ...(aprilTax ? [{ name: 'Tax', value: 'tax' }] : []),
  ];

  return (
    <>
      <PageTitle
        pageTitle={pageTitles[activeTab]}
        breadcrumbs={[{ label: 'Clients', href: '/advisor/dashboard/clients' }, { label: pageTitles[activeTab] }]}
        actionButtons={actionButtons}
        tabsProps={{
          activeTab,
          setActiveTab: setTab,
          tabs,
        }}
      />
      <PageContainer>
        <TabsPrimitive value={activeTab} onValueChange={setTab}>
          <TabsContent value="households">
            <ClientsPaginationProvider initialPageSize={25}>
              <HouseholdsContent />
            </ClientsPaginationProvider>
          </TabsContent>
          <TabsContent value="accounts">
            <AccountsContent />
          </TabsContent>
          {aprilTax && (
            <TabsContent value="tax">
              <TaxContent />
            </TabsContent>
          )}
        </TabsPrimitive>
      </PageContainer>
    </>
  );
}

function Clients(_props: LDProps) {
  useTrackPageView();

  return <ClientsPageTabs />;
}

const ClientsPage: NextPageWithLayout = withLDConsumer()(withAdvisorGuard(Clients));

export default ClientsPage;

ClientsPage.getLayout = function getLayout(page: ReactElement) {
  return <AdvisorDashTopNavLayout>{page}</AdvisorDashTopNavLayout>;
};
